import { type FormView } from '@/types/schema/views/FormView';
import { useViewMultipleRecordsQuery } from '@/hooks/api/queries/useViewMultipleRecordsQuery';
import { useViewRecordQuery } from '@/hooks/api/queries/useViewRecordQuery';
import { usePageContext } from '@/context/PageContext';

/**
 * Custom hook for fetching and managing record values for form views.
 *
 * This hook is useful if you want to retrieve the record values/rawValues for a form view anywhere in the form.
 * This can be useful if you need the values, or the raw values without the formatting applied to them for the form.
 *
 */
export function useFormRecordValues(view: FormView) {
  const { activePageRecordId, recordIdFromParams } = usePageContext();

  const pageRecordId = activePageRecordId || recordIdFromParams;

  // If this is an edit form and there is a record associated with the page, we use that record id to fetch the data
  const { data: viewRecordFromPage, isLoading: isViewRecordFromPageLoading } = useViewRecordQuery({
    view,
    enabled: view.action === 'update' && !!pageRecordId,
    options: {
      format: 'both'
    }
  });

  // If this is an edit form and there is no record associated with the page, we use the record id associated with the view to fetch the data
  const { data: viewRecordFromView, isLoading: isViewRecordFromViewLoading } =
    useViewMultipleRecordsQuery({
      view,
      enabled: view.action === 'update' && !pageRecordId,
      options: {
        format: 'both'
      }
    });

  const recordValues = viewRecordFromPage || viewRecordFromView?.records[0];
  const isLoading = isViewRecordFromPageLoading || isViewRecordFromViewLoading;

  return {
    recordValues,
    isLoading
  };
}
